import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { companiesApi, deviceSpecsApi } from '../api'
import { getErrorMessage } from '../api/client'
import type { DeviceSpecSummary } from '../types'

// 디바이스 명세서 목록 — 설비조사 뒤, 서버 세팅 전에 현장 단위로 작성하는 수집 설계 (Edge팀 전용).
// 이름·회사로 거르고, 머리를 눌러 정렬한다 (기본: 최근 수정 순). 행은 클릭·Enter 로 연다.
type SortKey = 'name' | 'company' | 'deviceCount' | 'tagCount' | 'machineCount' | 'updatedAt'
const COLUMNS: { key: SortKey; label: string; numeric?: boolean }[] = [
  { key: 'name', label: '명세서' },
  { key: 'company', label: '회사' },
  { key: 'deviceCount', label: '디바이스', numeric: true },
  { key: 'tagCount', label: '태그', numeric: true },
  { key: 'machineCount', label: '인터페이스', numeric: true },
  { key: 'updatedAt', label: '최근 수정' },
]

export default function DeviceSpecs() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'updatedAt', desc: true })

  const { data: specs = [], isLoading } = useQuery({
    queryKey: ['device-specs'],
    queryFn: () => deviceSpecsApi.getAll().then(r => r.data),
  })

  const q = query.trim().toLowerCase()
  const shown = useMemo(() => {
    const value = (s: DeviceSpecSummary): string | number =>
      sort.key === 'company' ? s.company?.name ?? '' : sort.key === 'updatedAt' ? new Date(s.updatedAt).getTime() : s[sort.key]
    return specs
      .filter(s => !q || s.name.toLowerCase().includes(q) || (s.company?.name ?? '').toLowerCase().includes(q))
      .sort((a, b) => {
        const x = value(a), y = value(b)
        const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), 'ko')
        return sort.desc ? -c : c
      })
  }, [specs, q, sort])

  const toggleSort = (key: SortKey) =>
    setSort(s => (s.key === key ? { key, desc: !s.desc } : { key, desc: key === 'updatedAt' || COLUMNS.some(c => c.key === key && c.numeric) }))

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-gray-900">디바이스 명세</h2>
          <p className="text-xs text-gray-500 mt-1">서버를 세팅하기 전에 현장별 수집 디바이스와 태그를 미리 작성합니다</p>
        </div>
        <div className="flex items-center gap-2">
          {specs.length > 0 && (
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="명세서·회사 검색" aria-label="명세서 검색"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-56 bg-surface" />
          )}
          <button onClick={() => setCreating(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover transition">
            + 새 명세서
          </button>
        </div>
      </div>

      <div className="bg-surface rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-xs">
            <tr>
              {COLUMNS.map(c => (
                <th key={c.key} aria-sort={sort.key === c.key ? (sort.desc ? 'descending' : 'ascending') : undefined}
                  className={`px-4 py-3 font-medium ${c.numeric ? 'text-right' : 'text-left'}`}>
                  <button onClick={() => toggleSort(c.key)} className="inline-flex items-center gap-1 hover:text-gray-900">
                    {c.label}
                    <span className={sort.key === c.key ? 'text-gray-700' : 'text-gray-300'}>{sort.key === c.key && !sort.desc ? '▲' : '▼'}</span>
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {shown.length === 0 && (
              <tr><td colSpan={COLUMNS.length} className="px-4 py-10 text-center text-sm text-gray-500">
                {isLoading ? '불러오는 중…' : q ? `'${query.trim()}' 와 맞는 명세서가 없습니다.` : (
                  <div className="space-y-3">
                    <p>아직 명세서가 없습니다. 설비조사를 마친 현장부터 디바이스와 태그를 적어 보세요.</p>
                    <button onClick={() => setCreating(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover transition">+ 새 명세서</button>
                  </div>
                )}
              </td></tr>
            )}
            {shown.map(s => (
              <tr key={s.id} tabIndex={0} onClick={() => navigate(`/device-specs/${s.id}`)}
                onKeyDown={e => { if (e.key === 'Enter') navigate(`/device-specs/${s.id}`) }}
                className="hover:bg-gray-50 cursor-pointer outline-none focus-visible:bg-blue-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400">
                <td className="px-4 py-3 font-medium text-gray-800">{s.name}</td>
                <td className="px-4 py-3 text-gray-600">{s.company?.name ?? <span className="text-gray-400">미지정</span>}</td>
                <td className="px-4 py-3 text-gray-600 text-right tabular-nums">{s.deviceCount}</td>
                <td className="px-4 py-3 text-gray-600 text-right tabular-nums">{s.tagCount}</td>
                <td className="px-4 py-3 text-gray-600 text-right tabular-nums">{s.machineCount}</td>
                <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{new Date(s.updatedAt).toLocaleString('ko-KR')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <CreateDialog onClose={() => setCreating(false)} onCreated={id => {
          qc.invalidateQueries({ queryKey: ['device-specs'] })
          navigate(`/device-specs/${id}`)
        }} />
      )}
    </div>
  )
}

function CreateDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [name, setName] = useState('')
  const [companyId, setCompanyId] = useState('')

  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: () => companiesApi.getAll().then(r => r.data),
  })

  const create = useMutation({
    mutationFn: () => deviceSpecsApi.create(name.trim(), companyId || null).then(r => r.data),
    onSuccess: spec => onCreated(spec.id),
  })

  const inputClass = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm'

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onMouseDown={onClose}>
      <div className="bg-surface rounded-xl p-6 w-full max-w-md shadow-xl" onMouseDown={e => e.stopPropagation()}>
        <h3 className="font-semibold text-gray-800 mb-4">새 명세서</h3>
        <div className="space-y-3">
          <label className="block">
            <span className="block text-xs text-gray-500 mb-1">명세서 이름 <span className="text-red-500">*</span></span>
            <input value={name} onChange={e => setName(e.target.value)} autoFocus placeholder="예: OO공장 압출 라인"
              className={inputClass} onKeyDown={e => { if (e.key === 'Enter' && name.trim()) create.mutate() }} />
          </label>
          <label className="block">
            <span className="block text-xs text-gray-500 mb-1">회사</span>
            <select value={companyId} onChange={e => setCompanyId(e.target.value)} className={inputClass}>
              <option value="">나중에 지정</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
        </div>
        {create.isError && <p className="text-xs text-red-600 mt-3">{getErrorMessage(create.error, '만들지 못했습니다.')}</p>}
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50 transition">취소</button>
          <button onClick={() => create.mutate()} disabled={!name.trim() || create.isPending}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover transition disabled:opacity-50">
            {create.isPending ? '만드는 중…' : '만들기'}
          </button>
        </div>
      </div>
    </div>
  )
}
